import React, { useState } from "react";
import { Box, IconButton } from "@mui/joy";
import {
  getTaskEditorBranchConnectorSx,
  taskEditorBranchColumnContentSx,
  taskEditorBranchColumnFillerSx,
  taskEditorBranchColumnSx,
  taskEditorBranchColumnsRowSx,
  taskEditorBranchConnectorStubSx,
  taskEditorBranchStemSx,
} from "./utils/taskEditorBranchFlowSx";

// 한 번에 보여줄 옵션 컬럼 수 — 4개 이상이면 좌우 화살표로 한 칸씩 슬라이드.
export const TASK_EDITOR_BRANCH_VISIBLE_COLUMNS = 3;

// 페이징 화살표 — 컬럼 영역 좌우 가터에 세로 중앙 배치 (위/아래에 두면
// 분기 카드 ↔ 가로바 사이 선이 끊긴다).
const pagerButtonSx = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
  minWidth: "28px",
  minHeight: "28px",
  borderRadius: "999px",
} as const;

// 페이징 시 화살표 자리(좌우 가터) 확보용.
const pagerGutterSx = { px: "32px" } as const;

interface TaskEditorBranchFlowProps {
  optionCount: number;
  // 옵션 1개 컬럼의 내용(패널/미리보기 블록 등) — 커넥터·페이징은 여기서 처리.
  renderColumnContent: (optionIndex: number) => React.ReactNode;
  getColumnTestId?: (optionIndex: number) => string;
  // false 면 하단 커넥터·합류 stem 생략 (합류할 다음 블록이 없을 때).
  showMerge?: boolean;
  mergeTestId?: string;
  pagerTestIdPrefix?: string;
}

// 분기(button_branch) 플로우차트식 가로 컬럼 — 편집 뷰 루트와 미리보기
// 정적 모드 공용. 분기 카드 아래 stem → 상단 커넥터에서 옵션 컬럼들로
// 갈라졌다가 하단 커넥터 → stem 으로 다시 모여 다음 블록으로 이어진다.
//
// 페이징은 전체 컬럼을 한 트랙에 두고 translateX 로 미는 슬라이드 방식 —
// 보이는 범위만 잘라 렌더하면 이동이 뚝뚝 끊기고 커넥터도 매번 다시
// 그려진다. 화면 밖 컬럼은 viewport(overflow hidden)가 가린다.
function TaskEditorBranchFlow({
  optionCount,
  renderColumnContent,
  getColumnTestId,
  showMerge = true,
  mergeTestId,
  pagerTestIdPrefix,
}: TaskEditorBranchFlowProps) {
  const [startIndex, setStartIndex] = useState(0);

  const visibleCount = Math.min(
    TASK_EDITOR_BRANCH_VISIBLE_COLUMNS,
    optionCount
  );
  const maxStart = Math.max(0, optionCount - visibleCount);
  // 옵션 삭제로 범위가 줄어든 경우에도 안전하도록 렌더 시점에 clamp.
  const start = Math.min(startIndex, maxStart);
  const hasPager = optionCount > visibleCount;

  return (
    <Box>
      <Box sx={taskEditorBranchStemSx} />
      <Box sx={{ position: "relative", ...(hasPager ? pagerGutterSx : {}) }}>
        {hasPager && (
          <IconButton
            size="sm"
            variant="plain"
            aria-label="이전 옵션"
            disabled={start === 0}
            data-testid={pagerTestIdPrefix && `${pagerTestIdPrefix}-prev`}
            onClick={() => setStartIndex(Math.max(0, start - 1))}
            sx={{ ...pagerButtonSx, left: 0 }}
          >
            <span
              style={{ fontSize: "18px" }}
              className="material-symbols-rounded"
            >
              chevron_left
            </span>
          </IconButton>
        )}
        <Box sx={{ overflow: "hidden" }}>
          <Box
            sx={{
              ...taskEditorBranchColumnsRowSx,
              transition: "transform 0.3s ease",
            }}
            style={{
              width: `${(optionCount * 100) / visibleCount}%`,
              transform: `translateX(-${(start * 100) / optionCount}%)`,
            }}
            data-testid={pagerTestIdPrefix && `${pagerTestIdPrefix}-track`}
          >
            {Array.from({ length: optionCount }, (_, optionIndex) => {
              const isFirst = optionIndex === 0;
              const isLast = optionIndex === optionCount - 1;
              return (
                <Box
                  key={optionIndex}
                  sx={taskEditorBranchColumnSx}
                  data-testid={getColumnTestId?.(optionIndex)}
                >
                  <Box
                    sx={getTaskEditorBranchConnectorSx("top", isFirst, isLast)}
                  >
                    <Box sx={taskEditorBranchConnectorStubSx} />
                  </Box>
                  <Box sx={taskEditorBranchColumnContentSx}>
                    {renderColumnContent(optionIndex)}
                  </Box>
                  {showMerge && (
                    <>
                      <Box sx={taskEditorBranchColumnFillerSx} />
                      <Box
                        sx={getTaskEditorBranchConnectorSx(
                          "bottom",
                          isFirst,
                          isLast
                        )}
                      >
                        <Box sx={taskEditorBranchConnectorStubSx} />
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
        {hasPager && (
          <IconButton
            size="sm"
            variant="plain"
            aria-label="다음 옵션"
            disabled={start >= maxStart}
            data-testid={pagerTestIdPrefix && `${pagerTestIdPrefix}-next`}
            onClick={() => setStartIndex(Math.min(maxStart, start + 1))}
            sx={{ ...pagerButtonSx, right: 0 }}
          >
            <span
              style={{ fontSize: "18px" }}
              className="material-symbols-rounded"
            >
              chevron_right
            </span>
          </IconButton>
        )}
      </Box>
      {showMerge && (
        <Box sx={taskEditorBranchStemSx} data-testid={mergeTestId} />
      )}
    </Box>
  );
}

export default TaskEditorBranchFlow;
